import React, { useEffect, useState } from 'react';
import { MessageCircle, X, Loader, MinusCircle } from 'lucide-react';

const ChatbotAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      const script = document.createElement('script');
      script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
      script.async = true;
      script.onload = () => {
        window.jotformEmbedHandler(
          "iframe[id='JotFormIFrame-0194ecec57367ab895ce37ec2bd7e5e937c8']",
          "https://www.jotform.com"
        );
        setTimeout(() => setIsLoading(false), 1000); // Ensure iframe is loaded
      };
      document.body.appendChild(script);

      // Simulated unread messages (remove in production)
      const interval = setInterval(() => {
        if (!isOpen || isMinimized) {
          setUnreadCount(prev => prev + 1);
        }
      }, 30000);

      return () => {
        clearInterval(interval);
        const scripts = document.querySelectorAll('script[src*="jotfor.ms"]');
        scripts.forEach(script => script.remove());
      };
    }
  }, [isOpen, isMinimized]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    setUnreadCount(0);
    setIsMinimized(false);
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    setIsMinimized(!isMinimized);
  };

  return (
    <div className="fixed bottom-0 right-0 z-[9999] flex flex-col items-end">
      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`relative mb-16 mr-4 transition-all duration-300 ease-in-out
            ${isMinimized ? 'h-12' : 'h-[600px]'} 
            w-[350px] bg-white rounded-lg shadow-2xl overflow-hidden
            transform ${isMinimized ? 'translate-y-[calc(100%-3rem)]' : 'translate-y-0'}`}
          style={{ transform: 'translateZ(0)' }}
        >
          {/* Chat Header */}
          <div 
            className="absolute top-0 w-full h-12 bg-blue-600 text-white flex items-center justify-between px-4 cursor-pointer"
            onClick={handleMinimize}
          >
            <div className="flex items-center space-x-2">
              <MessageCircle size={20} />
              <span className="font-medium">Chat with Alphonse AI</span>
            </div>
            <button
              onClick={handleMinimize}
              className="p-1 hover:bg-blue-700 rounded-full transition-colors"
              aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
            >
              <MinusCircle size={20} />
            </button>
          </div>

          {/* Chat Content */}
          {!isMinimized && (
            <div className="w-full h-full pt-12">
              {isLoading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="flex flex-col items-center space-y-2">
                    <Loader className="w-8 h-8 animate-spin text-blue-600" />
                    <p className="text-gray-600">Loading chat...</p>
                  </div>
                </div>
              ) : (
                <iframe
                  id="JotFormIFrame-0194ecec57367ab895ce37ec2bd7e5e937c8"
                  title="Alphonse (AI): Customer Support Agent"
                  allowTransparency="true"
                  allow="geolocation; microphone; camera; fullscreen"
                  src="https://agent.jotform.com/0194ecec57367ab895ce37ec2bd7e5e937c8?embedMode=iframe&background=1&shadow=1"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    minWidth: '100%',
                    maxWidth: '100%'
                  }}
                  scrolling="no"
                  frameBorder="0"
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={handleToggle}
        className={`fixed bottom-4 right-4 w-14 h-14 flex items-center justify-center rounded-full shadow-lg 
          transition-all duration-300 group
          ${isOpen 
            ? 'bg-red-500 hover:bg-red-600' 
            : 'bg-blue-500 hover:bg-blue-600'
          } text-white`}
        style={{ zIndex: 10000 }}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        <div className="relative">
          {!isOpen && unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
              {unreadCount}
            </span>
          )}
          {isOpen ? (
            <X size={24} className="transform group-hover:rotate-90 transition-transform duration-300" />
          ) : (
            <MessageCircle 
              size={24} 
              className="transform group-hover:scale-110 transition-transform duration-300" 
            />
          )}
        </div>
      </button>
    </div>
  );
};

export default ChatbotAgent;